// src/main.tsx 1

import { MiniKit } from "@worldcoin/minikit-js";
MiniKit.install("app_0e3f2e07cf3fb2e43fdddbb73d21d355");
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { SlicesProvider } from "@bridge-applications/provider-kit";
import { Slices } from "./state/slices/Slices.ts";
import { BrowserRouter } from "react-router-dom";
import "./logic/helpers/i18n";
import { PostHogProvider } from "posthog-js/react";
import { LoadingProvider } from "./components/common/LoadingScreen/LoadingScreen.tsx";
import { WorldAuthClient } from "@bridge-applications/world-auth";
import {
    ApiAuthAccessTokenResSchema,
    ApiAuthAppSessionReqSchema,
    ApiAuthAppSessionResSchema,
    ApiAuthCompleteSiweReqSchema,
    ApiAuthCompleteSiweResSchema,
    ApiAuthNonceResSchema,
} from "@bridge-applications/eh-types";
import { checkMaintenance } from "./logic/helpers/checkMaintenance.ts";
import MaintenancePage from "./components/pages/MaintenancePage/MaintenancePage.tsx";
import { API_BASE } from "./state/config.ts";
import * as Sentry from "@sentry/react";
import type { PostHogConfig } from "posthog-js";
import { telemetry } from "@bridge-applications/telemetry-js";

Sentry.init({
    dsn: "https://5f66836dfde21fafb9e34f7b56d6b990@o4508935011106817.ingest.de.sentry.io/4510126372618320",
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: true,
    sampleRate: 0.1,
});

telemetry.init({
    host: "https://ingest.bridge.cm",
    capturePageview: true,
    capturePageleave: "if_capture_pageview",
    captureHistoryEvents: true,
    projectId: "eh-client",
    capturePerformance: true,
    environment: import.meta.env.VITE_NODE_ENV,
    sampleRates: {
        default: 1, // ---custom events: 100%
        pageview: 1, // ---pageviews: 100%
        pageleave: 1, // ---pageleaves: 100%
        web_vitals: 0.5, // ---veb Vitals: 50%
        network_timing: 0.1, // ---network timing: 10%
    },
});

// ___if an admin has enabled a local bypass, skip maintenance entirely.
const shouldBypassMaintenance = (() => {
    try {
        return (
            typeof window !== "undefined" &&
            localStorage.getItem("eh__maintenance_bypass") === "1"
        );
    } catch {
        return false;
    }
})();

const options: Partial<PostHogConfig> = {
    api_host: import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_HOST, // ---cf reverse proxy
    ui_host: "https://eu.posthog.com",
    autocapture: true, // ---disable autocapture
    // ___we capture pageviews manually after consent is resolved to avoid any pre-consent capture.
    capture_pageview: true,
    capture_exceptions: false, // ---unnecessary with sentry
    capture_heatmaps: false, // ---not used
    capture_pageleave: true, // ---still needed for session-duration tracking, prob. 1 event per session
    capture_performance: false, // ---not used
    capture_dead_clicks: false, // ---not used
    disable_surveys: true, // ---not used
};

if (!import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY) {
    throw new Error("VITE_REACT_APP_PUBLIC_POSTHOG_KEY is not set");
}

if (
    !import.meta.env.VITE_NODE_ENV ||
    !["production", "development"].includes(import.meta.env.VITE_NODE_ENV)
) {
    throw new Error("VITE_NODE_ENV is not set");
}

const worldAuthClient = WorldAuthClient.init({
    baseUrl: API_BASE,
    urlPaths: {
        nonce: "/api/v1/auth/nonce",
        completeSiwe: "/api/v1/auth/complete-siwe",
        appSession: "/api/v1/auth/app-session",
        accessToken: "/api/v1/auth/access-token",
    },
    defaultStatement: "TODO: HELLO WORLD",
    miniKit: MiniKit,
    debug: true,
    customSchemas: {
        nonce: { res: ApiAuthNonceResSchema },
        completeSiwe: {
            req: ApiAuthCompleteSiweReqSchema,
            res: ApiAuthCompleteSiweResSchema,
        },
        appSession: {
            req: ApiAuthAppSessionReqSchema,
            res: ApiAuthAppSessionResSchema,
        },
        accessToken: { res: ApiAuthAccessTokenResSchema },
    },
});

console.log("worldAuthClient", worldAuthClient);

async function start() {
    try {
        const env =
            import.meta.env.VITE_MAINTENANCE_ENV === "production"
                ? "prod"
                : "dev";
        const maintenance = shouldBypassMaintenance
            ? 0 // ---falsy -> boot app
            : await checkMaintenance({
                  env,
              });
        console.log("[maintenance] maintenance", maintenance);

        const root = createRoot(document.getElementById("root")!);
        root.render(
            <PostHogProvider
                apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
                options={options}
            >
                {maintenance ? (
                    <MaintenancePage maintenanceEnd={maintenance} />
                ) : (
                    <SlicesProvider slices={Slices}>
                        <LoadingProvider>
                            <BrowserRouter>
                                <App />
                            </BrowserRouter>
                        </LoadingProvider>
                    </SlicesProvider>
                )}
            </PostHogProvider>,
        );
    } catch (err) {
        // ___on any error, just boot the app normally
        console.error("Failed maintenance check, booting app:", err);
        const root = createRoot(document.getElementById("root")!);
        root.render(
            <PostHogProvider
                apiKey={import.meta.env.VITE_REACT_APP_PUBLIC_POSTHOG_KEY}
                options={options}
            >
                <SlicesProvider slices={Slices}>
                    <LoadingProvider>
                        <BrowserRouter>
                            <App />
                        </BrowserRouter>
                    </LoadingProvider>
                </SlicesProvider>
            </PostHogProvider>,
        );
    }
}

start();
